import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

// SDK
import { upload } from '@webapp/sdk/assets';
import { AssetUploadResponse } from '@webapp/sdk/assets';

// Utils
import { promiseDelay } from '@webapp/utils/promise-delay';

// Store
import { CreateDialog } from '@webapp/store/ui/action-creators';
import { UpdateReleaseStoreError } from '@webapp/store/release/action-creators';

// Types
import { AssetFile } from '@common/types/data/asset-file';
import { DialogConfiguration } from '@webapp/types/ui/dialog-configuration';

// Components
import Upload from '@webapp/components/upload';
import { ButtonType } from '@components/actions/button';
import UploadItem from '@webapp/components/upload/upload-item';
import AttachmentIcon from '@components/basics/icons/attachment';
import UploadListItem from '@components/upload/upload-list-item';

interface UploadFilesCtrlProps {
  className?: string;
  uploadedFilename?: string;
  onError: (message: string) => void;
  onChange: (asset?: AssetFile) => void;
  openModal: (
    artworkId: string,
    callback: (value: any) => void
  ) => CreateDialog;
  sendAnalytics: (
    field: string,
    value: string,
    error: string,
    releaseId?: string
  ) => UpdateReleaseStoreError;
}

const buildDialog = (
  callback: (value: any) => void
): DialogConfiguration<ButtonType> => {
  return {
    title: <Translate id="UPLOAD.COVER.DELETE.MODAL.TITLE" />,
    text: <Translate id="UPLOAD.COVER.DELETE.MODAL.TEXT" />,
    actions: [
      { type: 'primary', label: 'No', value: false },
      { type: 'secondary', label: 'Yes', value: true },
    ],
    callback,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  openModal: (artworkId: string, callback: (value: any) => void) =>
    dispatch(new CreateDialog(artworkId, buildDialog(callback))),
  sendAnalytics: (
    field: string,
    value: string,
    error: string,
    releaseId?: string
  ) => dispatch(new UpdateReleaseStoreError(field, value, error, releaseId)),
});

export const UploadFilesCtrl: React.FC<UploadFilesCtrlProps> = (props) => {
  const [progress, setProgress] = React.useState(0);
  const [status, setStatus] = React.useState<'uploading' | 'done'>();

  const [filename, setFilename] = React.useState('');
  const [error, setError] = React.useState('');

  const [request, setRequest] = React.useState<AssetUploadResponse>();
  const validateImageFormat = (file: File) => {
    if (
      ![
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
      ].includes(file.type)
    ) {
      throw new Error('RELEASE.INVALID_FORMAT');
    }
  };

  const handleFile = async (files: FileList | File[]) => {
    const file = files[0];

    setError('');

    props.onError('');

    try {
      validateImageFormat(file);

      setStatus('uploading');
      setProgress(0);
      setFilename(file.name);

      const newRequest = await upload('artwork', file);

      setRequest(newRequest);

      newRequest.upload.progress.subscribe((p) => setProgress(p));
      await newRequest.upload.request;

      await promiseDelay(3000);

      setStatus('done');
      props.onChange({
        url: newRequest.url,
        id: newRequest.id,
        contentType: file.type,
        size: file.size,
        crc32: '',
        key: newRequest.key,
        created_at: new Date(),
        updated_at: new Date(),
        name: file.name,
      });
    } catch (e) {
      const error: any = e;
      setError(e ? error.message : 'RELEASE.UNKNOWN_ERROR');

      props.sendAnalytics(
        'Release Cover Art',
        '',
        error.message ? error.message : 'RELEASE.UNKNOWN_ERROR'
      );
      props.onError(e ? error.message : 'RELEASE.UNKNOWN_ERROR');
    }
  };

  const onCancel = () => {
    if (request) {
      request.upload.cancel();
    }

    setStatus(undefined);
    setProgress(0);
    setFilename('');
  };

  const getDragText = () => {
    switch (status) {
      case 'uploading':
        return <Translate id="UPLOAD.FILES.DRAG.REPLACE" />;
      case 'done':
        return <Translate id="UPLOAD.FILES.DRAG.REPLACE" />;
      default:
        return <Translate id="UPLOAD.FILES.DRAG.UPLOAD" />;
    }
  };

  return (
    <div className={props.className}>
      <Upload
        status={status}
        progress={progress}
        error={error}
        onChange={handleFile}
        noProgressline
        noWrapper
      >
        <UploadItem
          buttonType="secondary-dark"
          dragText={getDragText()}
          noBorderTop
          noBorderBotto={props.uploadedFilename || filename}
          icon={<AttachmentIcon />}
          accept=".pdf,.doc.,.docx"
          status={status}
          error={error}
          btnDisabled={status === 'uploading'}
          isMultiline
          onChange={(files: FileList) => handleFile(files)}
          onCancel={onCancel}
          noWrapper
        />
      </Upload>
      {props.uploadedFilename || filename ? (
        <UploadListItem
          className="uploaded-item"
          text={filename !== '' ? filename : props.uploadedFilename ?? ''}
          done={status === 'done'}
          uploading={status === 'uploading'}
          progress={progress}
          error={!!error}
          onRetry={() => null}
          onReplace={(files: FileList) => handleFile(files)}
          accept=".pdf,.doc.,.docx"
          onCancel={onCancel}
          disableRemove={false}
          disableSorting={true}
        />
      ) : null}
    </div>
  );
};

export default connect(
  null,
  mapDispatchToProps
)(styled(UploadFilesCtrl)`
  .uploaded-item {
    margin-top: 3px;
  }
`);
